import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow, PageHero } from "../components/Bits";
import { BEATS, EPISODES, MOTIFS } from "../data/show";

const BeatIndex = () => (
  <nav className="beat-index" data-testid="beat-index">
    <p className="slate mb-4">Jump to beat</p>
    <ol>
      {BEATS.map((b) => (
        <li key={b.id}><a href={`#${b.id}`} data-testid={`beat-anchor-${b.n}`}><span>{String(b.n).padStart(2, "0")}</span>{b.title}</a></li>
      ))}
    </ol>
  </nav>
);

const Pilot = () => (
  <section id="pilot" className="section scroll-mt-20" data-testid="pilot-section">
    <div className="wrap">
      <Reveal><Eyebrow>Episode 101 · Pilot</Eyebrow><h2 className="display text-3xl md:text-5xl mt-3">The Pilot Beat Sheet</h2>
        <p className="mt-4 max-w-2xl text-[var(--muted)]">Cold open, two act breaks and a tag. Fifteen beats, and each one is wilder than the last.</p></Reveal>
      <div className="grid lg:grid-cols-[260px_1fr] gap-12 mt-14">
        <aside className="hidden lg:block"><div className="sticky top-24"><BeatIndex /></div></aside>
        <div className="space-y-0">
          {BEATS.map((b) => (
            <Reveal key={b.id}>
              <article id={b.id} className="beat scroll-mt-24" data-testid={`beat-${b.n}`}>
                <span className="beat-n">{String(b.n).padStart(2, "0")}</span>
                <div>
                  <p className={`slate ${b.act.includes("Break") || b.act === "Midpoint" ? "text-[var(--coral)]" : ""}`}>{b.act}</p>
                  <h3 className="display text-2xl md:text-3xl mt-2">{b.title}</h3>
                  <p className="mt-3 leading-relaxed text-[var(--muted)]">{b.text}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Season = () => (
  <section id="season" className="section bg-ink text-white scroll-mt-20" data-testid="season-section">
    <div className="wrap">
      <Reveal><Eyebrow light>Season One</Eyebrow><h2 className="display text-3xl md:text-5xl mt-3">Ten Episodes, Rising Chaos</h2>
        <p className="mt-4 max-w-2xl text-white/65">Each episode's crisis is bigger than the one before.</p></Reveal>
      <div className="mt-14 divide-y divide-white/10 border-y border-white/10">
        {EPISODES.map((e, i) => (
          <Reveal key={e.n} delay={i * 0.03}>
            <article id={`ep-${e.n}`} className="episode scroll-mt-24" data-testid={`episode-${e.n}`}>
              <span className="slate text-[var(--sun)]">EP {e.n}</span>
              <h3 className="display text-2xl md:text-3xl">{e.title}</h3>
              <p className="text-white/70 text-sm md:text-base">{e.crisis}</p>
              <div className="flex flex-col gap-3">
              <div className="meter" aria-label={`Escalation ${e.level} of 10`} data-testid={`episode-${e.n}-meter`}>
                {Array.from({ length: 10 }).map((_, k) => <i key={k} className={k < e.level ? "on" : ""} />)}
              </div>
              <Link to={e.n === "101" ? "#pilot" : `/episodes/${e.n}`} className="text-link text-sm text-[var(--sun)]" data-testid={`episode-${e.n}-beats-link`}>Beat sheet <ArrowRight size={13} /></Link>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Motifs = () => (
  <section id="motifs" className="section scroll-mt-20" data-testid="motifs-section">
    <div className="wrap">
      <Reveal><Eyebrow>Running Gags</Eyebrow><h2 className="display text-3xl md:text-5xl mt-3">Recurring Escalation Motifs</h2></Reveal>
      <div className="grid md:grid-cols-2 gap-6 mt-14">
        {MOTIFS.map((m, i) => (
          <Reveal key={m.id} delay={i * 0.06} className={`motif ${i === 0 ? "md:col-span-2" : ""}`} data-testid={`motif-${m.id}`}>
            <p className="slate text-[var(--coral)]">{m.tag}</p>
            <h3 className="display text-2xl md:text-3xl mt-2">{m.title}</h3>
            <p className="mt-3 text-[var(--muted)] leading-relaxed">{m.text}</p>
            <ol className="ladder mt-6">{m.ladder.map((s, k) => <li key={s} style={{ opacity: 0.45 + k * 0.14 }}>{s}</li>)}</ol>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export default function Episodes() {
  return (
    <div data-testid="episodes-page">
      <PageHero testid="episodes-hero" eyebrow="Episodes · Beat Sheet" title="Every Episode Is a Premiere" sub="The complete pilot beat sheet, a Season One overview, and the running gags that escalate throughout the series." />
      <div className="wrap pt-10 flex flex-wrap gap-3">
        <a href="#pilot" className="chip" data-testid="jump-pilot">Pilot Beat Sheet</a>
        <a href="#season" className="chip" data-testid="jump-season">Season One</a>
        <a href="#motifs" className="chip" data-testid="jump-motifs">Escalation Motifs</a>
      </div>
      <Pilot />
      <Season />
      <Motifs />
    </div>
  );
}
